import { useEffect, useState } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { FieldType } from "@regal-voice/shared-types";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace with RV components in follow-up PR
import { Input, Typography, Select, Tooltip, Radio } from "antd";
import CheckableTag from "antd/lib/tag/CheckableTag";
import { clsx } from "clsx";
import dayjs, { Dayjs } from "dayjs";
import map from "lodash/map";
import remove from "lodash/remove";
import { useDispatch } from "react-redux";

import {
    RVFormFloatingLabelWrap,
    RVDatePickerDayJSFacade,
    RVCheckbox,
    getTimezoneOptions,
    Zone,
    RVFormItem,
    RVTypeaheadSelect,
} from "@regal-voice/ui";
import timepickerStyles from "@regal-voice/ui/components/atoms/RVTimePicker/RVTimePicker.module.scss";
import { useEventSchemaForTypeahead } from "ServerData/EventSchema/Hooks/useEventSchemaForTypeahead/useEventSchemaForTypeahead";
import { getEventFields } from "ServerData/EventSchema/queries";
import { setFields } from "Services/state/journeys/JourneyStateSlice";
import { NodeTriggerType } from "Types/Journey";

import { getEventType } from "../../../../../shared/helpers";
import { unflattenFields } from "../../Utils/utils";
import { ContactAttributeAutoCompleteFormItem } from "../AutoCompleteFormItems/ContactAttributeAutoComplete";

import type { EventSchemaOptionEntry } from "ServerData/EventSchema/eventSchemaTypes";

import styles from "./DelayFormItems.module.scss";

function InputNumber({
    value,
    onChange,
    label,
    ...rest
}: {
    value?: any;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
}) {
    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        // value * 1 will return value if value is a number, else NaN
        const newNumber = (e.target.value as any) * 1;
        if (Number.isNaN(newNumber)) {
            return;
        }
        onChange?.(e);
    }
    return (
        <div className={clsx("flex-row align-center", styles.delayInput)}>
            <Input value={value} onChange={handleChange} className={styles.inputNumber} {...rest} />
            <span>{label}</span>
        </div>
    );
}

function TimePickerItem({
    value,
    onChange,
    className,
}: {
    // onChange is added to props when a component is wrapped by Form.Item
    // to handle the flow of form data
    onChange?: (value: any) => void;
    value?: any;
    className?: string;
}) {
    const timeValue = value?.hours !== undefined ? dayjs().hour(value.hours).minute(value.minutes) : undefined;

    function onTimeChange(newTime: Dayjs | null) {
        onChange?.({ hours: newTime?.hour(), minutes: newTime?.minute() });
    }

    return (
        // TODO this should be replaced with an RVTimePicker
        <RVDatePickerDayJSFacade
            picker="time"
            onChange={onTimeChange}
            onSelect={onTimeChange}
            size="large"
            use12Hours={true}
            format={"h:mm A"}
            style={{ width: "100%" }}
            minuteStep={15}
            showNow={false}
            value={timeValue}
            // added this style to fix some styling issues
            // this component should be replaced with RVTimePicker
            className={clsx(timepickerStyles.timepicker, className)}
        />
    );
}

export function DelayFormItems({
    formValues = {},
    setCanSubmit,
    triggeringEvent,
}: {
    formValues: Record<string, any>;
    setCanSubmit: (b: boolean) => void;
    triggeringEvent: { eventType?: string; eventName?: string };
}): JSX.Element {
    // todo Replace wit useEventsAttributesFullListQuery
    // @see src/ServerData/EventSchema/Hooks/useEventsAttributesQuery/useEventsAttributesQuery.ts
    useQuery(getEventFields, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",
        skip: !triggeringEvent.eventType || !triggeringEvent.eventName,
        variables: {
            eventType: getEventType(triggeringEvent.eventType as NodeTriggerType),
            eventName: triggeringEvent?.eventName,
        },
        onCompleted(data) {
            const schemaOptions = (data.getEventSchema as Record<string, string>[])
                .filter((x) => x.fieldType === "datetime")
                .reduce((acc, curr) => {
                    acc[curr.fieldName] = {
                        fieldId: curr.fieldId,
                        fieldType: curr.fieldType as FieldType,
                    };
                    return acc;
                }, {} as EventSchemaOptionEntry);
            setEventSchema(schemaOptions);
        },
    });

    const {
        skipTimeOfDay,
        skipDaysOfWeek,
        selectedWeekDays,
        timezone,
        skipFromTime,
        skipToTime,
        delayType,
        delayPropertySource,
        contactAttribute,
        delayPropertyName,
    } = formValues;
    const [eventSchema, setEventSchema] = useState<EventSchemaOptionEntry>({});
    const [datetimeEventSchema, setDatetimeEventSchema] = useState<EventSchemaOptionEntry>(eventSchema);
    const datetimePropertyTypeahead = useEventSchemaForTypeahead({ eventSchema: datetimeEventSchema });
    const dispatch = useDispatch();
    const weekdays = [
        { value: "S", index: 6, key: "Sunday" },
        { value: "M", index: 0, key: "Monday" },
        { value: "T", index: 1, key: "Tuesday" },
        { value: "W", index: 2, key: "Wednesday" },
        { value: "Th", index: 3, key: "Thursday" },
        { value: "F", index: 4, key: "Friday" },
        { value: "Sa", index: 5, key: "Saturday" },
    ];

    const fromTimeInErrorState = skipTimeOfDay && !skipFromTime;
    const toTimeInErrorState = skipTimeOfDay && !skipToTime;
    const skipDaysInErrorState =
        skipDaysOfWeek && (!selectedWeekDays || selectedWeekDays.length === 0 || selectedWeekDays.length === 7);
    const isPropertyDelay = delayType === "property";

    if (
        ((skipTimeOfDay || skipDaysOfWeek) && !timezone) ||
        fromTimeInErrorState ||
        toTimeInErrorState ||
        skipDaysInErrorState
    ) {
        setCanSubmit(false);
    } else {
        setCanSubmit(true);
    }

    function onChangeSkipTimeChkbox(evt: any) {
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    skipTimeOfDay: evt.target.checked,
                    skipDaysOfWeek,
                    selectedWeekDays,
                    skipFromTime,
                    skipToTime,
                    timezone,
                })
            )
        );
    }

    function onChangeSkipDaysChkbox(evt: any) {
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    skipDaysOfWeek: evt.target.checked,
                    skipTimeOfDay,
                    selectedWeekDays,
                    skipFromTime,
                    skipToTime,
                    timezone,
                })
            )
        );
    }

    function onChangeSelectedDays(val: boolean, index: number) {
        let daysSelected = null;
        if (val) {
            daysSelected = new Set([...(selectedWeekDays ? selectedWeekDays : []), index]);
        } else {
            daysSelected = new Set([...(selectedWeekDays ? selectedWeekDays : [])]);
            daysSelected.delete(index);
        }
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    skipDaysOfWeek,
                    skipTimeOfDay,
                    selectedWeekDays: [...daysSelected],
                    skipFromTime,
                    skipToTime,
                    timezone,
                })
            )
        );
    }

    function datetimeAttributeFilter(x: Record<string, any>): Record<string, any> {
        // filter to only datetime types
        return Object.fromEntries(remove(Object.entries(x), (entry) => entry[1]?.fieldType === "datetime"));
    }

    useEffect(() => {
        setDatetimeEventSchema(datetimeAttributeFilter(eventSchema));
    }, [delayPropertyName, eventSchema]);

    return (
        <>
            <RVFormFloatingLabelWrap
                label="Delay Type"
                placeholder="Type"
                showFormLabel={true}
                initialValue="standard"
                name="delayType"
                className={styles.standardMargin}
            >
                <Select>
                    <Select.Option value="standard">Standard Delay</Select.Option>
                    <Select.Option value="property">Delay Using Time from Property</Select.Option>
                </Select>
            </RVFormFloatingLabelWrap>

            <Typography.Text strong>{isPropertyDelay ? "Delay Until" : "Delay For"}</Typography.Text>
            {isPropertyDelay && (
                <>
                    <RVFormFloatingLabelWrap
                        name="delayPropertySource"
                        placeholder="Property Source"
                        initialValue="triggeringEvent"
                        className={styles.standardMargin}
                    >
                        <Select>
                            <Select.Option value="triggeringEvent">Triggering Event</Select.Option>
                            <Select.Option value="contactAttribute">Contact Attribute</Select.Option>
                        </Select>
                    </RVFormFloatingLabelWrap>
                    {delayPropertySource === "contactAttribute" ? (
                        <ContactAttributeAutoCompleteFormItem
                            contactAttribute={contactAttribute}
                            filterAttributes={datetimeAttributeFilter}
                        />
                    ) : (
                        <RVFormFloatingLabelWrap
                            name="delayPropertyName"
                            placeholder="Select Property Name"
                            className={styles.standardMargin}
                            rules={[
                                {
                                    required: true,
                                    message: "Select Property",
                                },
                            ]}
                        >
                            <RVTypeaheadSelect {...datetimePropertyTypeahead} />
                        </RVFormFloatingLabelWrap>
                    )}
                    <RVFormFloatingLabelWrap noStyle name="delayOffsetType" initialValue="plus">
                        <Radio.Group className={styles.buttonGroup} size="small">
                            <Radio.Button className={clsx(styles.radio)} value="plus">
                                Plus
                            </Radio.Button>
                            <Radio.Button className={clsx(styles.radio)} value="minus">
                                Minus
                            </Radio.Button>
                        </Radio.Group>
                    </RVFormFloatingLabelWrap>
                </>
            )}
            <div className={clsx("flex-row align-center", styles.delayInput)}>
                <RVFormFloatingLabelWrap
                    name="days"
                    wrapperCol={{ span: 24 }}
                    rules={[
                        {
                            message: "Invalid days value",
                            pattern: /^([1-9]|[0-9]*)$/,
                        },
                    ]}
                >
                    <InputNumber label="Days" />
                </RVFormFloatingLabelWrap>
            </div>
            <div className={clsx("flex-row align-center", styles.delayInput)}>
                <RVFormFloatingLabelWrap
                    name="hours"
                    wrapperCol={{ span: 24 }}
                    rules={[
                        {
                            message: "Must be less than 24 hours",
                            pattern: /^([0-9]|1[0-9]|2[0-3])$/,
                        },
                    ]}
                >
                    <InputNumber label="Hours" />
                </RVFormFloatingLabelWrap>
            </div>
            <div className={clsx("flex-row align-center", styles.delayInput)}>
                <RVFormFloatingLabelWrap
                    name="minutes"
                    wrapperCol={{ span: 24 }}
                    rules={[
                        {
                            message: "Must be less than 60 minutes",
                            pattern: /^([0-9]|[1-5][0-9])$/,
                        },
                    ]}
                >
                    <InputNumber label="Minutes" />
                </RVFormFloatingLabelWrap>
            </div>
            <div className={clsx("flex-row align-center", styles.delayInput)}>
                <RVFormFloatingLabelWrap
                    name="seconds"
                    wrapperCol={{ span: 24 }}
                    rules={[
                        {
                            message: "Must be less than 60 seconds",
                            pattern: /^([0-9]|[1-5][0-9])$/,
                        },
                    ]}
                >
                    <InputNumber label="Seconds" />
                </RVFormFloatingLabelWrap>
            </div>
            <div className={clsx("flex-row", styles.nogap)}>
                <RVFormFloatingLabelWrap name="skipTimeOfDay" wrapperCol={{ span: 24 }}>
                    <RVCheckbox key="timeOfDay" onChange={onChangeSkipTimeChkbox} checked={skipTimeOfDay}>
                        <Typography>Skip specific times of day</Typography>
                    </RVCheckbox>
                    <Tooltip title="Extends delay until an allowed time of day">
                        <InfoCircleOutlined className={styles.infoIcon} />
                    </Tooltip>
                </RVFormFloatingLabelWrap>
            </div>
            {skipTimeOfDay && (
                <div className={clsx("flex-row align-center", styles.delayInput)}>
                    <div>
                        <Typography.Text strong>From</Typography.Text>
                        <RVFormItem name="skipFromTime" wrapperCol={{ span: 24 }}>
                            <TimePickerItem className={fromTimeInErrorState && clsx(styles.errorBorder)} />
                        </RVFormItem>
                    </div>
                    <div>
                        <Typography.Text strong>To</Typography.Text>
                        <RVFormItem name="skipToTime" wrapperCol={{ span: 24 }}>
                            <TimePickerItem className={toTimeInErrorState && clsx(styles.errorBorder)} />
                        </RVFormItem>
                    </div>
                </div>
            )}
            {(toTimeInErrorState || fromTimeInErrorState) && (
                <span className={clsx("flex-row align-center", styles.errorText, styles.delayInput)}>
                    Time is required.
                </span>
            )}

            <RVFormFloatingLabelWrap name="skipDaysOfWeek" wrapperCol={{ span: 24 }}>
                <RVCheckbox key="daysOfWeek" onChange={onChangeSkipDaysChkbox} checked={skipDaysOfWeek}>
                    <Typography>Skip specific days of week</Typography>
                </RVCheckbox>
                <Tooltip title="Extends delay by 24 hours for each day skipped until an allowed day of week">
                    <InfoCircleOutlined className={styles.infoIcon} />
                </Tooltip>
            </RVFormFloatingLabelWrap>
            {skipDaysOfWeek && (
                <div className={clsx("flex-row align-center", styles.delayInput, styles.normalgap)}>
                    <RVFormItem name="selectedWeekDays" hidden={true} />
                    {weekdays.map((item) => (
                        <CheckableTag
                            className={clsx(styles.checkableTag, {
                                [styles.checkableTagSelected]: selectedWeekDays?.indexOf(item.index) > -1,
                                [styles.errorBorder]: skipDaysInErrorState,
                            })}
                            key={item.key}
                            checked={selectedWeekDays?.indexOf(item.index) > -1}
                            onChange={(val) => onChangeSelectedDays(val, item.index)}
                        >
                            <span>{item.value}</span>
                        </CheckableTag>
                    ))}
                </div>
            )}
            {skipDaysInErrorState && (
                <span className={clsx("flex-row align-center", styles.errorText, styles.delayInput)}>
                    {selectedWeekDays?.length === 7 ? "Too many days selected." : "Select day(s) to skip."}
                </span>
            )}
            {(skipDaysOfWeek || skipTimeOfDay) && (
                <RVFormFloatingLabelWrap
                    label="Time Zone"
                    showFormLabel={true}
                    placeholder="Select time zone"
                    name="timezone"
                    wrapperCol={{ span: 24 }}
                    rules={[
                        {
                            required: true,
                            message: "Select time zone.",
                        },
                    ]}
                >
                    <Select value={timezone} popupClassName={styles.selectDropDown}>
                        {map(getTimezoneOptions(), (name: string, tz: Zone) => (
                            <Select.Option key={tz} value={tz}>
                                {name}
                            </Select.Option>
                        ))}
                        <Select.Option
                            className={clsx([styles.dynamicTimezoneItem], "flex-row justify-between")}
                            key="ContactOwnTimezone"
                            value="CONTACT_TIMEZONE"
                        >
                            <div className={styles.dynamicTimezoneEntry}>
                                <span>Contact&apos;s Local Time Zone</span>
                            </div>
                        </Select.Option>
                    </Select>
                </RVFormFloatingLabelWrap>
            )}
        </>
    );
}
